import React, { useEffect, useState, useMemo, act } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

import Tab from 'components/common/Tab';
import { CommonDateRange, CustomSelect } from 'components';
import { constant } from 'constants/constant';
import { getGpsVehicle } from '_services';
import CommonDateTimeRange from 'components/common/CommonDateTimeRange';
import { FaPlay, FaStop } from 'react-icons/fa6';
import StorageService from 'utils/StorageServices';
import RunningVehicleGps from './RunningVehicleGps';
import Loading from 'pages/Loading';
import { Images } from 'constants';
import { getGpsTimeline, getStoppageGps, getSummaryGps } from '_services/newGpsServices';
import { DatePickerComponent } from 'components/common/CommonDatePicker';
import { utils } from 'helper';
import CustomTooltip from "components/CustomTooltip/CustomTooltip";

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const NewGpsTimeline = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // Query params
    const tbn = query.get('tbn') || '1';
    const vn = query.get('vn') || '';
    const cmid = query.get('cmid') || '';

    const [isLoading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(Number(tbn));
    const [timeline, setTimeline] = useState([]);

    const [summaryData, setSummaryData] = useState([]);
    const [runingHistory, setRuningHistory] = useState([]);
    const [stoppage, setStoppage] = useState([]);

    const [vehicle, setVehicle] = useState([]);
    const [allVehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDateTimeRange, setSelectedDateTimeRange] = useState({
        startDate: moment().subtract(6, 'days').startOf('day').add(1, 'minute'),
        endDate: moment().endOf('day'),
    });
    // const [gpsDate, setGpsDate] = useState(new Date());

    // Handle tab changes
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        const params = new URLSearchParams(location.search);
        params.set('tbn', tabId);
        navigate(`${location.pathname}?${params.toString()}`);
    };

    // Fetch GPS timeline
    const fetchGpsTimeline = async () => {
        if (!cmid || !vn) return;

        const startDate = selectedDateTimeRange?.startDate
            ? moment(selectedDateTimeRange.startDate).startOf('day').add(1, 'minute')
            : moment().subtract(6, 'days').startOf('day').add(1, 'minute'); // 7-day range includes today

        const endDate = selectedDateTimeRange?.endDate
            ? moment(selectedDateTimeRange.endDate).endOf('day')
            : moment().endOf('day'); // today's end of day

        const payload = {
            company_id: cmid,
            vehicle_no: vn,
            // startDate: selectedDateTimeRange?.startDate
            //     ? moment(selectedDateTimeRange.startDate).format('YYYY-MM-DDTHH:mm:ssZ')
            //     : moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            // endDate: selectedDateTimeRange?.endDate
            //     ? moment(selectedDateTimeRange.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
            //     : moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            start_date: startDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            end_date: endDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            type: activeTab,
        };

        try {
            setLoading(true)
            const res = await getGpsTimeline(payload);
            if (res?.responseCode === 200) {
                setTimeline(res?.data?.timeline || []);
                setVehicle(res?.data?.vehicle || []);
            }
        } catch (error) {
            console.error('Error fetching GPS timeline', error);
        }
        finally {
            setLoading(false)
        }
    };

    const fetchTimelineGps = async () => {
        if (!cmid || !vn) return;

        const startDate = selectedDateTimeRange?.startDate
            ? moment(selectedDateTimeRange.startDate).startOf('day').add(1, 'minute')
            : moment().subtract(6, 'days').startOf('day').add(1, 'minute');

        const endDate = selectedDateTimeRange?.endDate
            ? moment(selectedDateTimeRange.endDate).endOf('day')
            : moment().endOf('day');

        const payload = {
            vehicleNo: vn,
            fromDate: startDate.format('YYYY-MM-DD'),
            toDate: endDate.format('YYYY-MM-DD'),
        };

        try {
            setLoading(true)
            const res = await getGpsTimeline(payload);
            if (res?.responseCode === 200) {
                setRuningHistory(res?.data)
            }
        } catch (error) {
            console.error('Error fetching GPS timeline', error);
        }
        finally {
            setLoading(false)
        }
    };

    const fetchSummaryGps = async () => {
        if (!cmid || !vn) return;

        const startDate = selectedDateTimeRange?.startDate
            ? moment(selectedDateTimeRange.startDate).startOf('day').add(1, 'minute')
            : moment().subtract(6, 'days').startOf('day').add(1, 'minute');

        const endDate = selectedDateTimeRange?.endDate
            ? moment(selectedDateTimeRange.endDate).endOf('day')
            : moment().endOf('day');

        const payload = {
            vehicleNo: vn,
            fromDate: startDate.format('YYYY-MM-DD'),
            toDate: endDate.format('YYYY-MM-DD'),
        };

        try {
            setLoading(true)
            const res = await getSummaryGps(payload);
            if (res?.responseCode === 200) {
                setSummaryData(res?.data);
            }
        } catch (error) {
            console.error('Error fetching GPS timeline', error);
        }
        finally {
            setLoading(false)
        }
    };

    const fetchStoppageGps = async () => {
        if (!cmid || !vn) return;

        const startDate = selectedDateTimeRange?.startDate
            ? moment(selectedDateTimeRange.startDate).startOf('day').add(1, 'minute')
            : moment().subtract(6, 'days').startOf('day').add(1, 'minute');

        const endDate = selectedDateTimeRange?.endDate
            ? moment(selectedDateTimeRange.endDate).endOf('day')
            : moment().endOf('day');

        const payload = {
            vehicleNo: vn,
            fromDate: startDate.format('YYYY-MM-DD'),
            toDate: endDate.format('YYYY-MM-DD'),
        };

        try {
            setLoading(true)
            const res = await getStoppageGps(payload);
            if (res?.responseCode === 200) {
                setStoppage(res?.data);
            }
        } catch (error) {
            console.error('Error fetching GPS timeline', error);
        }
        finally {
            setLoading(false)
        }
    };

    // Fetch vehicle list
    const fetchGpsVehicle = async () => {
        const payload = {
            company_id: cmid || StorageService.getCompanyId(),
            limit: 100,
            offset: 1,
            search: ''
        };

        try {
            const res = await getGpsVehicle(payload);
            if (res?.responseCode === 200) {
                setVehicles(res?.data?.vehicles || []);
            }
        } catch (error) {
            console.error('Error fetching GPS vehicles', error);
        }
    };

    // Trigger timeline fetch
    useEffect(() => {
        if (vn && activeTab === 0) {
            fetchSummaryGps();
        }
        if (vn && activeTab === 1) {
            fetchTimelineGps();
        }
        if (vn && activeTab === 3) {
            fetchStoppageGps();
        }
    }, [cmid, vn, selectedDateTimeRange.startDate, selectedDateTimeRange.endDate, activeTab]); // gpsDate

    // Trigger vehicle fetch on mount
    useEffect(() => {
        fetchGpsVehicle();
    }, []);

    // Sync vehicle dropdown with query params
    useEffect(() => {
        if (vn && cmid && allVehicles.length) {
            const matched = allVehicles.find(v => v.vehicle_no === vn);
            setSelectedVehicle(matched ? {
                label: matched.vehicle_no,
                value: matched.vehicle_no,
                cmid: matched.company_id
            } : {
                label: vn,
                value: vn,
                cmid
            });
        }
    }, [vn, cmid, allVehicles]);

    // Tab prop config
    const tabProps = {
        tabList: constant.newGpsTrackerTabs,
        callBackTab: handleTabChange,
        activeTab
    };

    // On vehicle select
    const handleVehicleSelect = (item) => {
        setSelectedVehicle(item);
        const params = new URLSearchParams(location.search);
        params.set('vn', item?.value);
        params.set('cmid', item?.cmid);
        navigate(`${location.pathname}?${params.toString()}`);
    };

    // On date range apply
    const handleApplyDateRange = (range) => {
        setSelectedDateTimeRange(range)
        // setSelectedDateTimeRange({
        //     startDate: range.startDate,
        //     endDate: range.endDate,
        // });
    };

    // Dropdown options
    const vehicleOptions = useMemo(() => (
        allVehicles.map((v) => ({
            label: v.vehicle_no,
            value: v.vehicle_no,
            cmid: v.company_id
        }))
    ), [allVehicles]);

    useEffect(() => {
        if (allVehicles?.length > 0) {
            setSelectedVehicle({
                label: allVehicles[0]?.vehicle_no,
                value: allVehicles[0]?.vehicle_no,
                cmid: allVehicles[0]?.company_id
            })
            const params = new URLSearchParams(location.search);
            params.set('vn', allVehicles[0]?.vehicle_no);
            params.set('cmid', allVehicles[0]?.company_id);
            navigate(`${location.pathname}?${params.toString()}`);
        }
    }, [allVehicles])

    return (
        <div className="__mainGps_bx">
            <div className="addPr_flBx">
                <div className="_secFilts_gps" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tab {...tabProps} />
                    <div className="llt_dash_branch_date">

                        <div className="__gpsDateRange">
                            {/* <DatePickerComponent
                                label="Date "
                                name="date"
                                showTimeSelect={false}
                                defaultTimeFix={false}
                                startDate={gpsDate}
                                onChange={(data) => setGpsDate(data)}
                            /> */}
                            <CommonDateRange
                                initialStartDate={selectedDateTimeRange?.start_date}
                                initialEndDate={selectedDateTimeRange?.end_date}
                                onApply={handleApplyDateRange}
                            />
                        </div>

                        <div className="__gpsVehicleSelect">
                            <CustomSelect
                                options={vehicleOptions}
                                name="vehicle_no"
                                label="Select Vehicle"
                                placeholder="Select Vehicle"
                                value={selectedVehicle}
                                onSelect={handleVehicleSelect}
                            />
                        </div>
                    </div>
                </div>

                {activeTab == 0 &&
                    <div className='__gpsSummary_bx'>
                        <div className='__gpsSummary_inr __gpsSummary_runningTab'>
                            <div className='__gpsSummary_item'>
                                <table>
                                    <thead>
                                        <tr>
                                            <td><div className='__txt_sphire'>Running Type</div></td>
                                            <td><div className='__txt_sphire'>Vehicle Avg</div></td>
                                            <td><div className='__txt_sphire'>KM Running</div></td>
                                            <td><div className='__txt_sphire'>GPS Fuel Consumed</div></td>
                                            <td><div className='__txt_sphire'>Running Duration</div></td>
                                            <td><div className='__txt_sphire'>Stopped Duration</div></td>
                                            <td><div className='__txt_sphire'>Ideal Duration</div></td>
                                            <td><div className='__txt_sphire'>GPS Inactive</div></td>
                                            <td><div className='__txt_sphire'>Alerts</div></td>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'> {(summaryData?.total_idle_distance_km + summaryData?.total_running_distance_km)?.toFixed(2)} </div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}></span></div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'>{summaryData?.total_running_time}</div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}></span></div></td>
                                            <td><div className='__txt_sphire'>{summaryData?.total_parked_time}</div></td>
                                            <td><div className='__txt_sphire'>{summaryData?.total_idle_time}</div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                        </tr>
                                    </tbody>

                                    <tfoot>
                                        <tr>
                                            <td><div className='__txt_sphire'>Total Running</div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'> {(summaryData?.total_idle_distance_km + summaryData?.total_running_distance_km)?.toFixed(2)} </div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}></span></div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'>{summaryData?.total_running_time}</div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}></span></div></td>
                                            <td><div className='__txt_sphire'>{summaryData?.total_parked_time}</div></td>
                                            <td><div className='__txt_sphire'>{summaryData?.total_idle_time}</div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                            <td><div className='__txt_sphire'> - </div></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                }

                {activeTab == 1 &&
                    <div className="__fltTier_tableBx">
                        <table>
                            <thead>
                                <tr>
                                    <th>Vehicle</th>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                    <th>Start Location</th>
                                    <th>End Location</th>
                                    <th>Distance</th>
                                    <th>Duration</th>
                                    <th>Avarage Speed</th>
                                    <th>Running Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ?
                                    <tr>
                                        <td colSpan={10}><div className='__fltHist_txs' style={{ textAlign: 'center' }}>Loading...</div></td>
                                    </tr>
                                    :
                                    runingHistory.length > 0 ? (
                                        runingHistory.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td><div className='__fltHist_txs'>{item?.vehicle_no}</div></td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{item?.fromDate ? moment(item?.fromDate).format('DD MMM yyyy') : '-'}</div>
                                                        <div className='__fltHist_txs'>{item?.fromTime}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{item?.toDate ? moment(item?.toDate).format('DD MMM yyyy') : '-'}</div>
                                                        <div className='__fltHist_txs'>{item?.toTime}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>
                                                            {(() => {
                                                                const name = item?.startLocation || "";
                                                                const value = String(name);

                                                                return value.length > 20 ? (
                                                                    <CustomTooltip
                                                                        txtClass="__tooltipTxt_bx"
                                                                        cstStyle={{ maxWidth: "138px" }}
                                                                    >
                                                                        <p>{value}</p>
                                                                    </CustomTooltip>
                                                                ) : (
                                                                    <div className="__NottooltipTxt">
                                                                        <p>{value ? value : '-'}</p>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>
                                                            {(() => {
                                                                const name = item?.endLocation || "";
                                                                const value = String(name);

                                                                return value.length > 20 ? (
                                                                    <CustomTooltip
                                                                        txtClass="__tooltipTxt_bx"
                                                                        cstStyle={{ maxWidth: "138px" }}
                                                                    >
                                                                        <p>{value}</p>
                                                                    </CustomTooltip>
                                                                ) : (
                                                                    <div className="__NottooltipTxt">
                                                                        <p>{value ? value : '-'}</p>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>
                                                    <td><div className='__fltHist_txs'>{item?.distance ? `${Number(item?.distance).toFixed(2)} KM` : '-'}</div></td>
                                                    {/* <td><div className='__fltHist_txs'>{(item?.fromDate && item?.fromTime && item?.toDate && item?.toTime) ? utils.getDuration(item?.fromDate, item?.fromTime, item?.toDate, item?.toTime) : '-'}</div></td> */}
                                                    {/* <td><div className='__fltHist_txs'> {(item?.fromDate && item?.fromTime && item?.toDate && item?.toTime && item?.distance) ? utils?.getAvarageSpeed(item?.fromDate, item?.fromTime, item?.toDate, item?.toTime, item?.distance) : '-'} </div></td> */}

                                                    <td><div className='__fltHist_txs'>{item?.duration ? item?.duration : '-'}</div></td>
                                                    <td><div className='__fltHist_txs'> {item?.average_speed ? `${item?.average_speed} km/h` : '-'} </div></td>

                                                    <td>
                                                        <div className='__empty_txs'>
                                                            {item?.status}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                    <img src={Images.NoRecordFound} alt="" />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </table>
                    </div>
                }

                {activeTab == 3 &&
                    <div className="__fltTier_tableBx">
                        <table>
                            <thead>
                                <tr>
                                    <th>Vehicle</th>
                                    <th>Stop Time</th>
                                    <th>Start Time</th>
                                    <th>Location</th>
                                    <th>Nearest Location</th>
                                    <th>After Distance</th>
                                    <th>Duration</th>
                                    <th>Avarage Speed</th>
                                    <th>Running Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ?
                                    <tr>
                                        <td colSpan={10}><div className='__fltHist_txs' style={{ textAlign: 'center' }}>Loading...</div></td>
                                    </tr>
                                    : stoppage.length > 0 ? (
                                        stoppage.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td><div className='__fltHist_txs'>{item?.vehicle_no ? item?.vehicle_no : '-'}</div></td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{item?.fromDate ? moment(item?.fromDate).format('DD MMM yyyy') : '-'}</div>
                                                        <div className='__fltHist_txs'>{item?.fromTime}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{item?.toDate ? moment(item?.toDate).format('DD MMM yyyy') : '-'}</div>
                                                        <div className='__fltHist_txs'>{item?.toTime}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'> - </div>
                                                    </td>
                                                    <td><div className='__fltHist_txs'> - </div></td>
                                                    <td><div className='__fltHist_txs'>{item?.distance ? `${Number(item?.distance).toFixed(2)} KM` : '-'}</div></td>
                                                    {/* <td><div className='__fltHist_txs'>{(item?.fromDate && item?.fromTime && item?.toDate && item?.toTime) ? utils.getDuration(item?.fromDate, item?.fromTime, item?.toDate, item?.toTime) : '-'}</div></td>
                                                    <td><div className='__fltHist_txs'> {(item?.fromDate && item?.fromTime && item?.toDate && item?.toTime && item?.distance) ? utils?.getAvarageSpeed(item?.fromDate, item?.fromTime, item?.toDate, item?.toTime, item?.distance) : '-'} </div></td> */}
                                                    <td><div className='__fltHist_txs'>{item?.duration ? item?.duration : '-'}</div></td>
                                                    <td><div className='__fltHist_txs'> {item?.average_speed ? `${item?.average_speed} km/h` : '-'} </div></td>
                                                    <td>
                                                        <div className='__empty_txs'>
                                                            {item?.status}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                    <img src={Images.NoRecordFound} alt="" />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </table>
                    </div>}


            </div>
        </div>
    );
};

export default NewGpsTimeline;
